import { Building2, Factory } from "lucide-react";
import type { ShiftsMode, WeeklyShift } from "@opus-dt/shared-types";
import { WEEKDAYS, minutesToTime } from "@/features/calendar/time-helpers";
import { cn } from "@/lib/utils";

interface Props {
  value: ShiftsMode;
  onChange: (mode: ShiftsMode) => void;
  /** Turnos da fábrica — o que a linha passa a usar ao herdar. `null` = fábrica sem expediente. */
  orgShifts: WeeklyShift[] | null;
  disabled?: boolean;
}

/** Resume os turnos numa frase, sem obrigar a abrir outra tela para saber o que se está herdando. */
function describe(shifts: WeeklyShift[]): string {
  if (shifts.length === 0) return "sem turnos declarados";
  const byWindow = new Map<string, number[]>();
  for (const s of shifts) {
    const key = `${minutesToTime(s.startMinute)}–${minutesToTime(s.endMinute)}`;
    const bucket = byWindow.get(key);
    if (bucket) bucket.push(s.weekday);
    else byWindow.set(key, [s.weekday]);
  }
  return [...byWindow.entries()]
    .map(([janela, dias]) => {
      const nomes = dias
        .sort((a, b) => a - b)
        .map((d) => WEEKDAYS.find((w) => w.value === d)?.short ?? d);
      return `${nomes.join(", ")} ${janela}`;
    })
    .join(" · ");
}

// A linha SEGUE o padrão da fábrica ou declara o próprio.
// A escolha é EXPLÍCITA, e não inferida de a lista de turnos estar vazia. Se "vazio" significasse
// "herda", não haveria como declarar uma linha desativada numa fábrica que opera — e é justamente
// esse o estado do meio do contrato de três estados.
// Herdar MOSTRA o que se está herdando. Um rádio que diz só "seguir o padrão da fábrica" obriga a
// abrir outra tela para descobrir que padrão é esse, no exato momento em que a decisão é tomada.
export function ShiftsModeChoice({ value, onChange, orgShifts, disabled }: Props) {
  const semFabrica = orgShifts === null;

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="text-[13px] font-medium">Expediente desta linha</legend>

      {/* Grid em vez de spans aninhados: o título e a descrição são filhos DIRETOS do <label>,
          que é o que torna o texto acessível ao rótulo do rádio (e o que o lint de a11y exige). */}
      <label
        htmlFor="shifts-mode-inherit"
        className={cn(
          "grid cursor-pointer grid-cols-[auto_1fr] items-start gap-x-2 gap-y-0.5 rounded-md border px-3 py-2 text-[12px]",
          value === "INHERIT" ? "border-rd-primary-500 bg-bg-muted-rd" : "border-border-subtle"
        )}
      >
        <input
          id="shifts-mode-inherit"
          type="radio"
          name="shifts-mode"
          className="accent-rd-primary-500 mt-1 h-3.5 w-3.5"
          checked={value === "INHERIT"}
          disabled={disabled}
          onChange={() => onChange("INHERIT")}
        />
        <span className="flex items-center gap-1.5 font-medium text-text-primary">
          <Building2 className="h-3.5 w-3.5 text-text-tertiary" aria-hidden />
          Seguir o padrão da fábrica
        </span>
        {/* Herdar de uma fábrica sem expediente não é erro, mas o efeito é contraintuitivo: a
            linha fica sem expediente e o tempo planejado volta a sair das ordens. */}
        <span className="col-start-2 text-text-secondary">
          {semFabrica
            ? "A fábrica ainda não declarou expediente. Enquanto não declarar, esta linha fica sem expediente e o tempo planejado sai das ordens de produção."
            : `Hoje: ${describe(orgShifts)}`}
        </span>
      </label>

      <label
        htmlFor="shifts-mode-override"
        className={cn(
          "grid cursor-pointer grid-cols-[auto_1fr] items-start gap-x-2 gap-y-0.5 rounded-md border px-3 py-2 text-[12px]",
          value === "OVERRIDE" ? "border-rd-primary-500 bg-bg-muted-rd" : "border-border-subtle"
        )}
      >
        <input
          id="shifts-mode-override"
          type="radio"
          name="shifts-mode"
          className="accent-rd-primary-500 mt-1 h-3.5 w-3.5"
          checked={value === "OVERRIDE"}
          disabled={disabled}
          onChange={() => onChange("OVERRIDE")}
        />
        <span className="flex items-center gap-1.5 font-medium text-text-primary">
          <Factory className="h-3.5 w-3.5 text-text-tertiary" aria-hidden />
          Declarar expediente próprio
        </span>
        <span className="col-start-2 text-text-secondary">
          Os turnos abaixo SUBSTITUEM os da fábrica — não somam a eles.
        </span>
      </label>
    </fieldset>
  );
}
